import Vue from 'vue'
import applogger from '@applogger'
import { PRODUCT_ID_QUERY } from '../queries/productIDQuery'
import fieldMapFactories from '../utils/fieldMapFactories'
import applyFieldMapper from '../utils/applyFieldMapper'
import createVariableMap from '../utils/createVariableMap'
import createTaxonomyMap from '../utils/createTaxonomyMap'

const state = {
  products: [],
  productStock: {},
  promises: {},
  maps: {
    taxonomy: {},
    variable: {}
  },
  mapSettings: {
    'wine_product': 'wine-product',
    'vc_shop_product': 'shop-product',
    'pos_product': 'pos-product',
    'pos_food_product': 'pos-food-product',
    'pos_modifier_product': 'pos-modifier-product'
  }
}

const actions = {
  store: function({ commit }, payload) {
    commit('SET_PRODUCTS', payload)
  },
  findByID: function({ dispatch, getters }, payload) {
    const product = getters.findProductByID(payload.id)

    return (product) || dispatch('loadProductByQuery', payload)
  },
  findByType: function(store, payload) {
    return store.getters.all.filter(product => product.type === payload)
  },
  loadProductByQuery: function(store, payload) {
    try {
      return store.dispatch('queryProductByID', payload.id)
        .then((queryResponse) => {
          const productFound = queryResponse.data && queryResponse.data.hasOwnProperty('product')
          const factoryMapRequested = payload.hasOwnProperty('map') && payload.map !== ''

          if (productFound) {
            if (factoryMapRequested) {
              return store.dispatch('applyFactoryFieldMap', {
                products: [queryResponse.data.product],
                map: payload.map,
                store: payload.store ?? store
              })
                .then((fieldMappedProduct) => {
                  const product = fieldMappedProduct.shift()

                  store.dispatch('addOrReplaceProduct', product)

                  return product
                })
            }
            else {
              store.dispatch('addOrReplaceProduct', queryResponse.data.product)
            }
          }

          return queryResponse.data.product ?? {}
        })
    }
    catch (_) {
      return {}
    }
  },
  storeProducts: function({ commit, getters }, payload) {
    commit('SET_PRODUCTS', payload)
  },
  addOrReplaceProduct: function({ commit }, payload) {
    commit('ADD_OR_REPLACE_PRODUCT', payload)
  },
  detectAndMapProduct: function({ dispatch, getters, rootGetters }, payload) {
    return dispatch('applyFactoryFieldMap', {
      product: payload,
      map: getters.findMapSetting(payload.type ?? ''),
      store: {
        dispatch,
        getters: rootGetters
      }
    })
  },
  queryProductByID: function({ getters }, payload) {
    return getters.graphQLClient.query({
      query: PRODUCT_ID_QUERY,
      variables: { id: Number(payload) }
    })
      .catch((err) => {
        console.error(`Error querying for product ID: ${payload} - ${err.message}`)
      })
  },
  queryAndStoreProduct: function({ dispatch }, payload) {
    return dispatch('queryProductByID', payload)
      .then((queriedProduct) => dispatch('detectAndMapProduct', queriedProduct.data.product))
      .then((mappedProduct) => dispatch('addOrReplaceProduct', mappedProduct))
      .catch((err) => console.error(`Unable to query and store product: ${err.message}`))
  },
  applyFactoryFieldMap(store, payload) {
    const productSupplied = payload.hasOwnProperty('product') && typeof payload.product === 'object' && JSON.stringify(payload.product) !== '{}'
    const mapperSupplied = payload.hasOwnProperty('map')

    return (productSupplied && mapperSupplied)
      ? store.dispatch('generateMappedProduct', payload)
      : payload.product ?? {}
  },
  generateMappedProduct(store, payload) {
    return store.dispatch('generateFieldMap', payload.map)
      .then((fieldMap) => {
        return (fieldMap !== null)
          ? applyFieldMapper(fieldMap, (payload.store ?? store), payload.product)
          : payload.product ?? {}
      })
  },
  loadMapper(_, requestedMap) {
    try {
      const availableFactoryMaps = Object.keys(fieldMapFactories)

      return availableFactoryMaps.includes(requestedMap)
        ? fieldMapFactories[requestedMap]
        : ''
    }
    catch (err) {
      console.error(err.message)
      return null
    }
  },
  generateFieldMap(store, payload) {
    return store.dispatch('loadMapper', payload)
      .then((fieldMapGenerator) => {
        if (fieldMapGenerator !== null && typeof fieldMapGenerator === 'function') {
          return fieldMapGenerator(
            store.getters['getMap']('taxonomy'),
            store.getters['getMap']('variable')
          )
        }
        else {
          return {}
        }
      })
  },
  loadAllMaps: function({ dispatch }, queryResults) {
    dispatch('loadTaxonomyMap', queryResults)
    dispatch('loadVariableMap', queryResults)
  },
  loadTaxonomyMap: function({ dispatch }, payload) {
    return dispatch('storeTaxonomyMap', createTaxonomyMap(payload))
  },
  loadVariableMap: function({ dispatch }, payload) {
    return dispatch('storeVariableMap', createVariableMap(payload))
  },
  storeTaxonomyMap: function({ commit }, payload) {
    commit('SET_MAP', {
      key: 'taxonomy',
      value: payload
    })
  },
  storeVariableMap: function({ commit }, payload) {
    commit('SET_MAP', {
      key: 'variable',
      value: payload
    })
  },
  currentStock: function({ state, commit, rootState, rootGetters }, payload) {
    if (payload !== undefined && payload.force !== undefined && !payload.force) {
      if ((payload.itemid !== undefined && Object.prototype.hasOwnProperty.call(state.productStock, payload.itemid))) {
        return Promise.resolve(state.productStock[payload.itemid])
      }
    }

    let url = rootGetters.getServiceEndpoint('datastore').concat(rootState.registry.getServiceSetting('inventory', 'stock'))
    let itemid
    if (!Object.prototype.hasOwnProperty.call(state.promises, url)) {
      if (payload) {
        if (payload.itemid !== undefined) {
          itemid = payload.itemid
        }
        else if (payload.nid !== undefined) {
          itemid = payload.nid
        }
        url = url.concat('/', itemid)
      }

      if (!Object.prototype.hasOwnProperty.call(state.promises, url)) {
        state.promises[url] = fetch(url, {
          method: 'GET'
        })
          .then(resp => {
            if (resp.ok) {
              return resp.json()
            }
            throw new Error(`Error loading ProductStock: ${url} - ${resp.status} :: ${resp.statusText}`)
          })
          .then(data => data.data)
          .catch(err => {
            if (itemid) {
              applogger.error(`Unable to load product stock for Item: ${itemid} - ${err.message}`)
            }
            else {
              applogger.error(`Unable to load product stock for all Items: ${err.message}`)
            }
          })
          .then(productStock => {
            if (itemid === undefined) {
              Object.entries(productStock).forEach(([itemid, stock]) => {
                commit('UPDATE_PRODUCT_STOCK', { id: itemid, ...stock })
              })
            }
            else {
              commit('UPDATE_PRODUCT_STOCK', { id: itemid, ...productStock })
            }
          })
      }
    }

    return state.promises[url]
      .then(_ => {
        if (itemid !== undefined) {
          return state.productStock[itemid].manage_stock ? state.productStock[itemid].stock : 9999999
        }
      })
      .catch(_err => {
        return 0
      })
      .finally(_ => {
        delete state.promises[url]
      })
  }
}

const getters = {
  all(state) {
    return state.products
  },
  findMapSetting: (state) => (productType) => {
    return state.mapSettings.hasOwnProperty(productType)
      ? state.mapSettings[productType]
      : ''
  },
  graphQLClient: function(state, getters, rootState, rootGetters) {
    return rootGetters['getGraphQLClient']()
  },
  productCatalogue: (state, getters) => (catalogue = '') => {
    const filteringIsRequired = catalogue !== ''

    return filteringIsRequired
      ? getters.allProducts.filter(product => product.catalogue === catalogue)
      : getters.allProducts
  },
  findProductByID: (_state, getters) => (payload) => {
    return getters.all.find(product => product.id === payload)
  },
  findByRoutePath: (_state, getters) => (payload) => {
    const product = getters.all.filter(product => (product.path === payload || product.path_prefixed_slash === payload))

    return product.length > 0
      ? product.shift()
      : null
  },
  getMap: (state) => (payload) => {
    return state.maps.hasOwnProperty(payload)
      ? state.maps[payload]
      : null
  },
  productsByItemID: function(state) {
    return state.products.reduce((acc, item) => ({ ...acc, [item.id]: item }), {})
  },
  currentStock: function(state) {
    return (itemid) => {
      if (Object.prototype.hasOwnProperty.call(state.productStock, itemid)) {
        return state.productStock[itemid].manage_stock ? state.productStock[itemid].stock : 999999
      }
      return 9999999
    }
  },
  managedStock: function(state) {
    return (itemid) => {
      if (Object.prototype.hasOwnProperty.call(state.productStock, itemid)) {
        return !!state.productStock[itemid].manage_stock
      }
      return false
    }
  },
  productSettings: (_state, getters) => (itemid) => {
    if (Object.prototype.hasOwnProperty.call(itemid, 'id')) {
      itemid = itemid.id
    }
    if (Object.prototype.hasOwnProperty.call(getters.productsByItemID, itemid)) {
      return getters.productsByItemID[itemid].product_settings
    }
    return {}
  },
  prices: function(_state, getters) {
    return (itemid) => {
      if (Object.prototype.hasOwnProperty.call(itemid, 'id')) {
        itemid = itemid.id
      }
      if (Object.prototype.hasOwnProperty.call(getters.productsByItemID, itemid)) {
        return {
          ...getters.productsByItemID[itemid].product_settings.prices,
          price: getters.productsByItemID[itemid].product_settings.price,
          rrp: getters.rrpPrice(itemid),
          sell_price: getters.sellPrice(itemid)
        }
      }
      return {
        rrp: 0,
        price: 0
      }
    }
  },
  basePrice: function(_state, getters) {
    return (itemid) => {
      if (Object.prototype.hasOwnProperty.call(itemid, 'id')) {
        itemid = itemid.id
      }
      if (Object.prototype.hasOwnProperty.call(getters.productsByItemID, itemid)) {
        return getters.productsByItemID[itemid].product_settings.prices.base_price
      }
      applogger.error(`Product Not found (basePrice): ${itemid}`)
      return 0
    }
  },
  rrpPrice: function(_state, getters) {
    return (itemid) => {
      if (Object.prototype.hasOwnProperty.call(itemid, 'id')) {
        itemid = itemid.id
      }
      if (Object.prototype.hasOwnProperty.call(getters.productsByItemID, itemid)) {
        if (getters.productsByItemID[itemid].wineproductrrp > 0) {
          return getters.productsByItemID[itemid].wineproductrrp
        }
        return getters.productsByItemID[itemid].product_settings.base_price
      }
      applogger.error(`Product Not found (rrpPrice): ${itemid}`)
      return 0
    }
  },
  unitPrice: function(_state, getters, _rootState, _rootGetters) {
    return (itemid) => getters.sellPrice(itemid)
  },
  sellPrice: function(_state, getters, _rootState, rootGetters) {
    return (itemid, account) => {
      if (Object.prototype.hasOwnProperty.call(itemid, 'id')) {
        itemid = itemid.id
      }
      if (Object.prototype.hasOwnProperty.call(getters.productsByItemID, itemid)) {
        try {
          return getters.productsByItemID[itemid].product_settings.prices.base_price +
            rootGetters['ecommerce/getTotalPriceAdjustment'](getters.productsByItemID[itemid], account)
        }
        catch (err) {
          return getters.productsByItemID[itemid].product_settings.base_price
        }
      }
      applogger.error(`Product Not found (sellPrice): ${itemid}`)
      return 0
    }
  },
  packPrice: function(_state, getters) {
    return (itemid) => {
      if (Object.prototype.hasOwnProperty.call(itemid, 'id')) {
        itemid = itemid.id
      }
      return (getters.sellPrice(itemid) * 6)
    }
  },
  packPriceLabel: function() {
    return (itemid) => {
      return '6 Bottles.'
    }
  },
  rrpPackPrice: function(_state, getters) {
    return (itemid) => {
      if (Object.prototype.hasOwnProperty.call(itemid, 'id')) {
        itemid = itemid.id
      }
      return (getters.rrpPrice(itemid) * 6)
    }
  },
  cartMultipleHandling: function(_state, getters) {
    return (itemid) => {
      if (Object.prototype.hasOwnProperty.call(getters.productsByItemID, itemid)) {
        const value = Object.prototype.hasOwnProperty.call(getters.productsByItemID[itemid], 'cart_multiple') ? getters.productsByItemID[itemid].cart_multiple : 'sameprice'
        switch (value) {
        case '0': // The actual value of cart mutiple internally in Drupal is "0"
          return 'sameprice'
        case '1':
          return 'never'
        case '2':
          return 'always'
        default:
          return value
        }
      }
      return 'sameprice'
    }
  },
  allowInCart: function(state, getters) {
    return (itemid) => {
      if (Object.prototype.hasOwnProperty.call(itemid, 'id')) {
        itemid = itemid.id
      }
      return getters.productsByItemID[itemid].allow_in_cart
    }
  },
  productSpecials: function(_state, getters, _rootState, rootGetters) {
    return (itemid) => {
      if (Object.prototype.hasOwnProperty.call(itemid, 'id')) {
        itemid = itemid.id
      }
      const adjustments = rootGetters['ecommerce/getPriceAdjustments'](getters.productsByItemID[itemid])
      const specials = Object.values(adjustments)
        .map(special => {
          if (Object.keys(special).length > 0) {
            return { label: special.description, type: special.display }
          }
          else return {}
        })
        .filter(value => Object.keys(value).length !== 0)
      return specials
    }
  },
  hasSavings: function(_state, getters, _rootState, rootGetters) {
    return (itemid) => {
      if (Object.prototype.hasOwnProperty.call(itemid, 'id')) {
        itemid = itemid.id
      }
      return getters.basePrice(itemid) - getters.unitPrice(itemid)
    }
  },
  customQty: function(_state, getters, rootState, rootGetters) {
    return (itemid) => {
      if (Object.prototype.hasOwnProperty.call(itemid, 'id')) {
        itemid = itemid.id
      }
      const productSettings = getters['productSettings'](itemid)
      if (productSettings.customqty !== undefined) {
        const customQuantities = rootGetters['ecommerce/getConfigItem']('custom_qty', 'settings')
        const settings = Object.values(customQuantities).reduce((acc, x) => ({ ...acc, [x.qty_key]: x }), {})

        return productSettings.customqty.map(cur => {
          // Check the Custom Qty Settings format for the GraphQL Response or the RestAPI Response.
          if (typeof cur === 'string') {
            return settings[cur]
          }
          else {
            try {
              return customQuantities[cur.id]
            }
            catch (err) {
              return null
            }
          }
        }).filter(_ => _)
      }
      else {
        return []
      }
    }
  },
  maxCart: function(_state, getters) {
    return (itemid) => {
      try {
        let item = null
        if (Object.prototype.hasOwnProperty.call(itemid, 'id')) {
          item = itemid
        }
        else if (Object.prototype.hasOwnProperty.call(getters.productsByItemID, itemid)) {
          item = getters.productsByItemID[itemid]
        }

        if (Object.prototype.hasOwnProperty.call(item, 'max_cart') && item.max_cart > 0) {
          return item.max_cart
        }
        else if (Object.prototype.hasOwnProperty.call(item, 'field_n_wine_prod_max_cart') && ['string', 'number'].includes(typeof item.field_n_wine_prod_max_cart)) {
          return parseInt(item.field_n_wine_prod_max_cart)
        }
        else if (Object.prototype.hasOwnProperty.call(item, 'field_n_wine_prod_max_cart') && Array.isArray(item.field_n_wine_prod_max_cart) && !isNaN(parseInt(item.field_n_wine_prod_max_cart[0].value))) {
          return parseInt(this.item.field_n_wine_prod_max_cart[0].value)
        }
        else {
          return 999999
        }
      }
      catch (_err) {
        return 999999
      }
    }
  }
}

const mutations = {
  ADD_PRODUCT: (state, payload) => state.products.push(payload),
  ADD_OR_REPLACE_PRODUCT: (state, payload) => {
    const noProducts = state.products.length <= 0
    const indexOfProduct = state.products.findIndex(product => Number(product.id) === Number(payload.id))
    const productNotStored = indexOfProduct === -1

    if (noProducts || productNotStored) {
      state.products.push(payload)
    }
    else {
      Vue.set(state.products, indexOfProduct, payload)
    }
  },
  SET_PRODUCTS: (state, payload) => {
    state.products = payload
  },
  ADD_PRODUCTS: (state, payload) => {
    Vue.set(state, 'products', state.products.concat(payload))
  },
  SET_MAP: (state, payload) => {
    state.maps[payload.key] = payload.value
  },
  REMOVE_PRODUCT_BY_ID: (state, payload) => {
    const existingProducts = JSON.parse(JSON.stringify(state.products))
    const filteredProducts = existingProducts.filter(product => product.id === payload)

    Vue.set(state, 'products', filteredProducts)
  },
  UPDATE_PRODUCT_STOCK: function(state, payload) {
    Vue.set(state.productStock, payload.id, payload)
  }
}

export default {
  namespaced: true,
  getters,
  state,
  actions,
  mutations
}
